import { useNavigate } from '@tanstack/react-router';
import { SearchForm } from '../components/SearchForm';
import { FeaturedParks } from '../components/FeaturedParks';
import { MainLayout } from '../layouts/MainLayout';
import type { SearchParams } from '../types';
import { MapPin, Bell, Shield } from 'lucide-react';

export function LandingPage() {
  const navigate = useNavigate();

  const handleSearch = (params: SearchParams) => {
    navigate({
      to: '/search',
      search: {
        location: params.location || undefined,
        startDate: params.startDate || undefined,
        guests: params.guests || undefined,
        nights: params.nights || undefined,
      },
    });
  };

  return (
    <MainLayout>
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Find Your Perfect <span className="text-emerald-600">Campground</span>
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Discover campsites across state and national parks nationwide.
            Set availability alerts and never miss out on your dream camping spot.
          </p>
        </div>

        <div className="max-w-xl mx-auto mb-16">
          <SearchForm onSearch={handleSearch} />
        </div>

        <FeaturedParks />

        <div className="grid md:grid-cols-3 gap-8">
          <FeatureCard
            icon={<MapPin className="w-8 h-8" />}
            title="Find Campsites"
            description="Search through hundreds of campgrounds and find the perfect spot for your next adventure."
          />
          <FeatureCard
            icon={<Bell className="w-8 h-8" />}
            title="Get Notified"
            description="Set availability alerts and get instantly notified when a spot opens up at your favorite campground."
          />
          <FeatureCard
            icon={<Shield className="w-8 h-8" />}
            title="Book with Confidence"
            description="Real-time availability data with direct links to official reservation systems nationwide."
          />
        </div>
      </div>
    </MainLayout>
  );
}

function FeatureCard({ icon, title, description }: { icon: React.ReactNode; title: string; description: string }) {
  return (
    <div className="text-center p-6">
      <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-100 text-emerald-600 rounded-2xl mb-4">
        {icon}
      </div>
      <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
      <p className="text-gray-600">{description}</p>
    </div>
  );
}
